import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useAppCreateMutation, useAppDeleteMutation } from 'hooks/mutations/exp'
import { expAppsOverviewRoute } from 'pages/NWPlatform/ExpApps'
import { expAppRoute } from 'pages/NWPlatform/ExpApps/ExpApp'
import { appQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { expAppId } = expAppRoute.useParams()
  const navigate = useNavigate({ from: expAppRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const expAppQuery = useQuery(appQueryKeys.detail({ appId: expAppId }))
  const { data: expApp } = expAppQuery

  const { mutate: createExpApp, ...expAppCreateMutation } = useAppCreateMutation()
  const { mutate: deleteExpApp } = useAppDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!expApp) return

    setAnchorEl(undefined)

    deleteExpApp(
      { appId: expApp.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:ExpApp.nameDelete', { name: expApp.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: expAppsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteExpApp, enqueueSnackbar, expApp, navigate, t])

  const handleDuplicateExpApp = useCallback(() => {
    if (!expApp) return

    setAnchorEl(undefined)

    createExpApp(
      { expAppInsert: { ...expApp, name: `${expApp.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [expApp, createExpApp, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={expApp?.name}
        entity={t('entity:exp.expApp', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateExpApp}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {expAppCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={expAppId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete staticLabel={t('domain:ExpApp.deleteExpApp', { count: 1 })} onConfirm={handleDeletion} />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
